<template>
	<div>
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<v-row>
					<v-col cols="12">
						<div class="form-base__title">{{ label }}</div>
					</v-col>

					<v-col cols="4">
						<v-text-field
							v-model="v.lastName.$model"
							:errorMessages="getValidationErrorText(v.lastName)"
							label="Фамилия"
							outlined
							@blur="v.lastName.$touch"
						/>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model="v.firstName.$model"
							:errorMessages="getValidationErrorText(v.firstName)"
							label="Имя"
							outlined
							@blur="v.firstName.$touch"
						/>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model="v.patronymic.$model"
							:errorMessages="getValidationErrorText(v.patronymic)"
							label="Отчество (при наличии)"
							outlined
							@blur="v.patronymic.$touch"
						/>
					</v-col>

					<v-col cols="4">
						<v-text-field
							v-model="v.snils.$model"
							:errorMessages="getValidationErrorText(v.snils)"
							label="СНИЛС"
							v-mask="['###-###-### ##']"
							outlined
							@blur="v.snils.$touch"
						/>
					</v-col>
					<v-col cols="4">
						<v-text-field
							v-model="v.inn.$model"
							:errorMessages="getValidationErrorText(v.inn)"
							label="ИНН"
							v-mask="['############']"
							outlined
							@blur="v.inn.$touch"
						/>
					</v-col>

					<v-col cols="6">
						<v-text-field
							v-model="v.trusteeStatus.$model"
							:errorMessages="getValidationErrorText(v.trusteeStatus)"
							:label="trusteeStatusLabel"
							outlined
							@blur="v.trusteeStatus.$touch"
						/>
					</v-col>
				</v-row>

				<v-col cols="12">
					<div class="form-base__title">
						Контактные данные {{ contactsRoleLabel }}
					</div>
				</v-col>

				<v-row>
					<v-col cols="5">
						<v-text-field
							v-model="v.email.$model"
							:errorMessages="getValidationErrorText(v.email)"
							:label="'Адрес электронной почты ' + contactsRoleLabel"
							outlined
							@blur="v.email.$touch"
						/>
					</v-col>
					<v-col cols="5">
						<v-text-field
							v-model="v.phone.$model"
							:errorMessages="getValidationErrorText(v.phone)"
							v-mask="['+7 ### ###-##-##']"
							:label="'Контактный телефон ' + contactsRoleLabel"
							outlined
							@blur="v.phone.$touch"
						/>
					</v-col>
				</v-row>

				<StatusDocument :v="v.documents.statusDocument" :statement-type="statementType"/>
				<AuthorityDocument :v="v.documents.authorityDocument" :statement-type="statementType"/>
			</div>
		</div>
	</div>
</template>

<script>
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {mdiAlertCircleOutline} from '@mdi/js';
import {
	EMAIL,
	EMOJI,
	getValidationErrorText,
	INN,
	MAX_LENGTH,
	MAX_LENGTH_EMAIL,
	MinLength,
	PERSON_NAME, TRAILING_AND_LEADING_SPACES,
} from '@/utils/validate';
import {DocumentType} from '@/types/services';
import StatusDocument, {
	getStatusDocumentValidation,
	statusDocumentModel,
} from '@/components/services/elements/StatusDocument';
import AuthorityDocument, {
	authorityDocumentValidation,
	authorityDocumentModel,
} from '@/components/services/elements/AuthorityDocument';
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import {Role} from '@/utils/dictionaries';

export const trusteeValidate = (trustee) => ({
	lastName: {
		required,
		maxLength: maxLength(MAX_LENGTH),
		PERSON_NAME,
		TRAILING_AND_LEADING_SPACES
	},
	firstName: {
		required,
		maxLength: maxLength(MAX_LENGTH),
		PERSON_NAME,
		TRAILING_AND_LEADING_SPACES
	},
	patronymic: {
		maxLength: maxLength(MAX_LENGTH),
		PERSON_NAME,
		TRAILING_AND_LEADING_SPACES
	},
	snils: {
		required,
		minLength: minLength(MinLength.SNILS),
	},
	inn: {
		minLength: minLength(12),
		required,
		INN: INN(12),
	},
	trusteeStatus: {
		maxLength: maxLength(1000),
		EMOJI,
		TRAILING_AND_LEADING_SPACES
	},
	email: {
		EMAIL,
		maxLength: maxLength(MAX_LENGTH_EMAIL),
		TRAILING_AND_LEADING_SPACES
	},
	phone: {
		minLength: minLength(16),
	},
	documents: {
		statusDocument: getStatusDocumentValidation(trustee),
		authorityDocument: authorityDocumentValidation,
	},
	statementType: {
		type: String,
		required: true,
	},
});

export const trusteeModel = {
	lastName: '',
	firstName: '',
	patronymic: '',
	snils: '',
	inn: '',
	trusteeStatus: '',
	email: '',
	phone: '',
	documents: {
		statusDocument: cloneDeep(statusDocumentModel),
		authorityDocument: cloneDeep(authorityDocumentModel),
	},
};

export const getDefaultManualTrustee = (trustee) => {
	return deepmerge(trustee, {
		documents: {
			statusDocument: trustee.documents.statusDocument ?? statusDocumentModel,
		},
	});
};

export const assemblyManualTrustee = (trustee) => {
	return deepmerge(trustee, {
		documents: {
			statusDocument: trustee.documents.statusDocument.name
				? trustee.documents.statusDocument
				: null,
		},
	});
};

export default {
	name: 'TrusteeView',
	components: {
		StatusDocument,
		AuthorityDocument,
	},
	props: {
		v: {
			type: Object,
			required: true,
		},
		label: {
			type: String,
			required: false,
			default:
				'Сведения о представителе физического лица (при направлении заявления представителем)',
		},
		trusteeStatusRequired: {
			type: Boolean,
		},
		roleType: {
			type: String,
			required: false,
		},
		statementType: {
			type: String,
			default: null,
		},
	},
	data: () => ({
		mdiAlertCircleOutline,
		getValidationErrorText,
		DocumentType,
	}),
	computed: {
		trusteeStatusLabel() {
			const label = 'Должность или статус представителя';

			return this.trusteeStatusRequired ? label : `${label} (при наличии)`;
		},
		contactsRoleLabel() {
			if (this.roleType !== null) {
				if (this.roleType === Role.ORGANIZATION) {
					return 'представителя юридического лица';
				} else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
					return 'представителя иностранного юридического лица';
				}
			}
			return '';
		},
	},
};
</script>
